import React, { useState } from "react";
import { AlertCircle, Bike, Loader2, MapPin, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import freightService from "@/services/freightService";
import addressService from "@/services/addressService";
import { showError } from "@/store/errorStore";

/** "Rua X, Bonfim, Salvador/BA" a partir do retorno do ViaCEP */
const formatAddress = (address) =>
  address
    ? [
        address.street,
        address.neighborhood,
        address.city && address.state
          ? `${address.city}/${address.state}`
          : address.city,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

/**
 * Consulta de frete pelo CEP de destino (GET /frete/{cep}/calcular),
 * card "Consultar Frete por CEP" de docs/tela/code.html.
 */
export default function FreightCalculator() {
  const [cep, setCep] = useState("");
  const [cepError, setCepError] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [address, setAddress] = useState(null);

  const handleCepChange = (value) => {
    setCep(freightService.formatCep(value));
    if (cepError) setCepError("");
    // Resultado antigo não vale para o novo CEP
    if (result) {
      setResult(null);
      setAddress(null);
    }
  };

  const handleCalculate = async (e) => {
    e.preventDefault();

    if (freightService.cleanCep(cep).length !== 8) {
      setCepError("Informe um CEP com 8 dígitos");
      return;
    }

    setCalculating(true);
    setResult(null);
    setAddress(null);
    try {
      const [freight, cepAddress] = await Promise.all([
        freightService.calculateFreight(cep),
        // Endereço é apenas informativo: falha na consulta não bloqueia o cálculo
        addressService.searchByCEP(cep).catch(() => null),
      ]);
      setResult(freight);
      setAddress(cepAddress);
    } catch (error) {
      // Erros HTTP (ex.: 400 com a mensagem do backend) já aparecem no modal global
      if (!error?.response && !error?.request) {
        showError(error?.message || "Erro ao calcular frete", "Frete");
      }
    } finally {
      setCalculating(false);
    }
  };

  const addressText = formatAddress(address);
  const destination = freightService.formatCep(result?.destinationCep || cep);
  const hasDistance =
    result?.distanceKm !== null &&
    result?.distanceKm !== undefined &&
    Number.isFinite(Number(result.distanceKm));

  return (
    <section className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-soft md:p-5">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-pistachio/30 text-primary">
          <Bike className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold text-foreground">
            Consultar Frete por CEP
          </h2>
          <p className="truncate text-xs text-muted-foreground">
            Simule o raio e o custo real de envio
          </p>
        </div>
      </div>

      <form
        onSubmit={handleCalculate}
        className="flex flex-col gap-2 sm:flex-row"
        noValidate
      >
        <div className="relative flex-1">
          <label htmlFor="freight-cep" className="sr-only">
            CEP de destino
          </label>
          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            id="freight-cep"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="00000-000"
            value={cep}
            onChange={(e) => handleCepChange(e.target.value)}
            disabled={calculating}
            maxLength={9}
            aria-invalid={Boolean(cepError)}
            aria-describedby={cepError ? "freight-cep-error" : undefined}
            className={cn(
              "h-11 w-full rounded-lg border border-transparent bg-surface pl-10 pr-3 text-sm text-foreground transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:bg-card focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 disabled:opacity-70",
              cepError && "border-destructive",
            )}
          />
        </div>
        <button
          type="submit"
          disabled={calculating}
          className="flex h-11 items-center justify-center gap-2 rounded-lg bg-primary px-5 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
        >
          {calculating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          {calculating ? "Calculando..." : "Calcular frete"}
        </button>
      </form>

      {cepError && (
        <p
          id="freight-cep-error"
          className="flex items-center gap-1 text-xs text-destructive"
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {cepError}
        </p>
      )}

      <div aria-live="polite">
        {result &&
          (result.available ? (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-surface p-3">
              <div className="flex min-w-0 items-start gap-2">
                <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                <div className="min-w-0">
                  <p
                    className="truncate text-xs font-semibold text-foreground"
                    title={addressText || undefined}
                  >
                    {addressText || `CEP ${destination}`}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {hasDistance &&
                      `${freightService.formatDistance(result.distanceKm)} de distância`}
                    {hasDistance && addressText && " • "}
                    {addressText && `CEP ${destination}`}
                  </p>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-baseline gap-1.5">
                <span className="text-[11px] font-semibold text-muted-foreground">
                  Taxa:
                </span>
                <span className="font-display text-base font-bold text-primary">
                  {freightService.formatCurrency(result.freightValue)}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-lg bg-terra-soft p-3 text-terra">
              <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-terra" />
              <div className="min-w-0 text-xs">
                <p className="font-semibold">Entrega indisponível para este CEP</p>
                {addressText && <p className="mt-0.5 truncate">{addressText}</p>}
                <p className="mt-0.5">
                  {result.message ||
                    "Não há taxa de frete cadastrada para esta distância."}
                </p>
                {hasDistance && (
                  <p className="mt-0.5">
                    Distância calculada:{" "}
                    {freightService.formatDistance(result.distanceKm)}
                  </p>
                )}
              </div>
            </div>
          ))}
      </div>
    </section>
  );
}
